'use client';

import { useEffect, useRef } from 'react';
import { toast } from 'sonner';

const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

export function useVersionCheck() {
  const initialBuildId = useRef<string>('');
  const toastShown = useRef(false);

  useEffect(() => {
    async function fetchBuildId(): Promise<string> {
      try {
        const res = await fetch('/api/version', { cache: 'no-store' });
        if (!res.ok) return '';
        const { buildId } = await res.json();
        return buildId ?? '';
      } catch {
        return '';
      }
    }

    async function checkVersion() {
      if (toastShown.current) return;

      const buildId = await fetchBuildId();

      // Skip only if the response was empty/malformed
      if (!buildId) return;

      // First call — capture baseline; do NOT show toast
      if (!initialBuildId.current) {
        initialBuildId.current = buildId;
        return;
      }

      // Subsequent calls — compare against baseline
      if (buildId !== initialBuildId.current) {
        toastShown.current = true;
        toast.info('New version available!', {
          description: 'A new version of Delfin has been deployed. Reload to get the latest.',
          duration: Infinity, // persistent — user must act
          action: {
            label: 'Reload',
            onClick: () => window.location.reload(),
          },
        });
      }
    }

    // Capture baseline immediately on mount
    checkVersion();

    // Poll on a fixed interval
    const interval = setInterval(checkVersion, POLL_INTERVAL_MS);

    // Also check when the user returns to the tab
    function onVisibilityChange() {
      if (document.visibilityState === 'visible') {
        checkVersion();
      }
    }
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);
}
